<script setup lang="ts">
import {onMounted, onUnmounted, ref, computed} from "vue";
import MonitorOverview from "../../components/monitor/pages/MonitorOverview.vue";
import MonitorOrderStats from "../../components/monitor/pages/MonitorOrderStats.vue";

const INTERVAL_MS = 60_000

const pages = [MonitorOverview, MonitorOrderStats]

const current_index = ref(0)
const current_page = computed(() => pages[current_index.value])

let interval: ReturnType<typeof setInterval>

onMounted(() => {
    interval = setInterval(() => {
        current_index.value = (current_index.value + 1) % pages.length
    }, INTERVAL_MS)
})

onUnmounted(() => {
    clearInterval(interval)
})
</script>
<template>
    <Transition name="fade" mode="out-in">
        <component :is="current_page" :key="current_index"/>
    </Transition>
</template>
<style lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.monitor-container {
    padding: 7.6rem 6rem 5rem;
    height: 100vh;
    background: var(--color-darth-vader);
    display: flex;
    flex-direction: column;

    .monitor-overview-container {
        margin-top: 6.6rem;
        display: flex;
        gap: 8.8rem;

        .monitor-pill {
            width: 100%;
            padding: 2rem 5.8rem 2rem 2rem;
            min-height: 19rem;
            background: var(--color-darth-maul);
            border-radius: 9.1rem;
            display: flex;
            flex-direction: column;
            gap: 1.2rem;

            &-content {
                display: flex;
                gap: 2rem;
                align-items: center;

                .monitor-pill-icon-block {
                    height: 15rem;
                    width: 15rem;
                    background: var(--color-darth-sidious);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .icon {
                        fill: var(--color-white);
                        width: 6.6rem;
                        height: 6.6rem;
                    }
                }

                .monitor-pill-info {
                    flex: 1;
                    display: flex;
                    justify-content: space-between;
                    gap: 1rem;
                    align-items: center;

                    p {
                        color: var(--color-white);

                        &.monitor-pill-title {
                            @include font(3.4, 3.4, black);
                        }

                        &.monitor-pill-stat {
                            @include font(8.4, 8.4, book);
                        }
                    }
                }
            }

            &-detail {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                grid-template-rows: repeat(2, 1fr);
                grid-column-gap: 2rem;
                grid-row-gap: 1rem;
                margin-bottom: 1.666rem;

                &-pill {
                    background: #192D39;
                    padding: 2rem;
                    height: 10rem;
                    border-radius: 8rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    &-label {
                        @include font(1.2,1.2,black);
                        color: var(--color-white-100);
                    }

                    &-value {
                        @include font(2.4,2.4,medium);
                        color: var(--color-white-100);
                    }
                }
            }

            &-sub {
                height: 18.2rem;
                border-top: .2rem solid var(--color-darth-vader);
                display: flex;

                &-item {
                    padding: 6rem 3.6rem 5.6rem 5.4rem;
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    &:first-of-type {
                        border-right: .2rem solid var(--color-darth-vader);
                    }

                    &-title {
                        @include font(2.5, 2.5, black);
                    }

                    &-value {
                        @include font(6.5, 2.5, book);
                    }
                }
            }

            &.--is-today {
                background: var(--color-tiffany-dark);
            }

            &.--is-danger {
                .monitor-pill-icon-block {
                    background: var(--color-secondary-orange);
                }

                .monitor-pill-stat {
                    color: var(--color-secondary-orange);
                }
            }

            &.--has-sub {
                .monitor-pill-content {
                    padding-right: 3.3rem;
                }
            }
        }

        .monitor-overview-item {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 2rem;
        }

        &-row {
            display: flex;
            gap: 2rem;
            width: 100%;
        }
    }

    &.--stats-monitor-view {
        padding: 3.6rem 6rem 5rem;

        .monitor-overview-container {
            flex-direction: column;
            gap: 3.021rem;
            margin-top: 3.3rem;

            .monitor-pill {
                padding: 1.434rem 3rem 1.434rem 1.434rem;
                min-height: unset;

                &-content {
                    .monitor-pill-icon-block {
                        width: 11.4rem;
                        height: 11.4rem;

                        .icon {
                            fill: var(--color-white);
                            width: 5.1rem;
                            height: 5.1rem;
                        }
                    }

                    .monitor-pill-info {
                        .monitor-pill-title {
                            @include font(2.6, 2.6, black);
                        }

                        .monitor-pill-stat {
                            @include font(4.8, 4.8, book);
                        }
                    }
                }
            }

            .monitor-overview-item {

                &:first-of-type {
                    width: calc(33.33% - 2rem);
                    flex: unset;
                }
            }
        }
    }
}
</style>
